import React, { useEffect } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { GridColDef } from '@mui/x-data-grid';
import AppDataGrid from '@/components/common/AppDataGrid';
import { store } from '@/service/store';
import { useSignals } from "@preact/signals-react/runtime";
import { formatDate } from '@/utils/formatters';
import { deviceTypeDict } from '@/constants';

interface CustomerDevicesTabProps {
  customerId: string;
}

interface Device {
  uuid: string;
  type: string;
  lastPingAt: string;
  manufacturerNumber: string;
  vendor: string;
}

const CustomerDevicesTab: React.FC<CustomerDevicesTabProps> = ({ customerId }) => {
  useSignals();

  const { t } = useTranslation();

  let customer = store.customers.get(customerId);
  const loading = store.customers.isLoading.value || customer?.devices?.isLoading?.value;

  useEffect(() => {
    const loadDevices = async () => {
      if (!customer) {
        await store.customers.load();
        customer = store.customers.get(customerId);
      }
      try {
        await customer.devices.load();
      } catch (error) {
        console.error('Error loading devices:', error);
      }
    };

    loadDevices();
  }, [customerId]);

  const columns: GridColDef[] = [
    {
      field: 'manufacturerNumber',
      headerName: t('devices.table.deviceSerial'),
      flex: 1.5,
      minWidth: 150,
    },
    {
      field: 'type',
      headerName: t('devices.table.deviceType'),
      flex: 1,
      minWidth: 120,
      valueGetter: (value: string) => deviceTypeDict[value] || value,
    },
    {
      field: 'vendor',
      headerName: t('devices.vendor'),
      flex: 1,
      minWidth: 120,
    },
    {
      field: 'lastPingAt',
      headerName: t('devices.lastPingAt'),
      flex: 1.5,
      minWidth: 180,
      valueFormatter: (params: any) => formatDate(params.value),
    }
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!customer?.devices.data.value || (customer?.devices.data.value?.length ?? 0) === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body1">{t('devices.noDevices')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', width: '100%' }}>
      <AppDataGrid
        rows={customer?.devices.data.value || []}
        columns={columns}
        density={"compact"}
        getRowId={(row) => row.uuid}
        loading={loading}
        autoHeight={false}
        sx={{ height: '100%' }}
      />
    </Box>
  );
};

export default CustomerDevicesTab;
